import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-stone-300">
      <button
        onClick={onClick}
        className="w-full py-5 md:py-6 flex items-center justify-between text-left transition-colors px-4 md:px-6"
        aria-expanded={isOpen}
      >
        <h3
          className="font-playfair text-lg md:text-xl font-medium text-stone-800 pr-4"
          style={{ textWrap: "balance" }}
        >
          {question}
        </h3>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <ChevronDown className="w-5 h-5 text-stone-400 shrink-0" aria-hidden="true" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-4 md:px-6 pb-5 md:pb-6">
              <p
                className="font-sourcesans text-base md:text-lg text-stone-700 leading-relaxed"
                style={{ textWrap: "pretty" }}
              >
                {answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Where does the 70% go?",
      answer:
        "70% of proceeds go directly to trusted individuals in Ukraine who support humanitarian aid efforts, including clothing, vehicle repair, communications devices, and food distribution.",
    },
    {
      question: "What happens to the other 30%?",
      answer:
        "The remaining 30% covers website hosting, transaction fees, materials, and helps us continue creating and bringing you more products.",
    },
    {
      question: "What is string art?",
      answer:
        "String art is embroidery on cardstock. We start by choosing a design, carefully punching holes, and then using string art techniques to sew the pattern onto the card, creating beautiful geometric designs.",
    },
    {
      question: "What materials do you use?",
      answer:
        "We use 100% tree-free cardstock and envelopes, along with premium satin and cotton DMC threads. Our commitment to sustainable materials reflects our values of care for both people and planet.",
    },
    {
      question: "Do you ship internationally?",
      answer:
        "Yes! We can ship internationally. If you're outside the US, please reach out to us first so we can provide shipping details and costs for your location.",
    },
    {
      question: "How long does shipping take?",
      answer:
        "Please allow 2–3 days for us to prepare your handmade card. Once ready, we ship it as soon as possible. Delivery time depends on your location.",
    },
    {
      question: "What do the cards look like?",
      answer:
        'Our cards are A2 size (4.25" × 5.5"), blank inside for your personal message, and come with matching envelopes. Each card features a unique string art design.',
    },
    {
      question: "Do you accept custom orders?",
      answer:
        "Yes, we'd love to create something special for you! Please reach out to discuss your custom design ideas and we'll work together to bring your vision to life.",
    },
    {
      question: "How should I care for my card?",
      answer:
        "Our string art cards can be framed and displayed as keepsake art pieces. Please avoid water contact to preserve the integrity of the materials.",
    },
  ];

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#f5f3ea] md:pt-56 pt-28 py-12 md:py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-playfair text-3xl md:text-5xl font-medium text-stone-800 mb-4">
            FAQ
          </h1>
          <p
            className="font-sourcesans text-base md:text-lg text-stone-600 leading-relaxed max-w-2xl mx-auto"
            style={{ textWrap: "balance" }}
          >
            Have questions about our handmade cards or mission?
          </p>
          <div className="mt-6 w-16 h-[2px] bg-stone-800 mx-auto rounded-full" />
        </motion.div>

        {/* FAQ Items */}
        <motion.div
          className="bg-[#f5f3ea] shadow-xl overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => handleToggle(index)}
            />
          ))}
        </motion.div>

        {/* Contact CTA */}
        <motion.div
          className="text-center mt-12 md:mt-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p
            className="font-sourcesans text-base md:text-lg text-stone-700 mb-4"
            style={{ textWrap: "balance" }}
          >
            Still have questions?
          </p>
          <Link
            to="/contact"
            className="inline-block font-sourcesans text-base md:text-lg text-stone-800 border-b-2 border-stone-800 hover:text-stone-600 hover:border-stone-600 transition-colors"
          >
            Get in touch
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;